import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { Callback } from "@/data/contractor/networkExecution/callback";

// ── DB row shape — matches callbacks table ────────────────────────────
interface CallbackRow {
  id: string;
  job_id: string;
  type: string;
  severity: string;
  source: string;
  attributed_to: string;
  owner_contractor_id_at_callback: string;
  installer_contractor_id_at_callback: string | null;
  filed_at: string;
  filed_by: string;
  description: string;
  organization_id: string;
  created_at: string;
}

// ── Mappers ───────────────────────────────────────────────────────────
function toUiCallback(row: CallbackRow): Callback {
  return {
    id: row.id,
    jobId: row.job_id,
    type: row.type as Callback["type"],
    severity: row.severity as Callback["severity"],
    source: row.source as Callback["source"],
    attributedTo: row.attributed_to as Callback["attributedTo"],
    ownerContractorIdAtCallback: row.owner_contractor_id_at_callback,
    installerContractorIdAtCallback: row.installer_contractor_id_at_callback,
    filedAt: row.filed_at,
    filedBy: row.filed_by,
    description: row.description,
  };
}

function toDbRow(
  input: Omit<Callback, "id"> & { id?: string },
  organizationId: string | null,
): Omit<CallbackRow, "created_at"> {
  return {
    id: input.id ?? `cb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    job_id: input.jobId,
    type: input.type,
    severity: input.severity,
    source: input.source,
    attributed_to: input.attributedTo,
    owner_contractor_id_at_callback: input.ownerContractorIdAtCallback,
    installer_contractor_id_at_callback: input.installerContractorIdAtCallback ?? null,
    filed_at: input.filedAt,
    filed_by: input.filedBy,
    description: input.description,
    organization_id: organizationId as string,
  };
}

// ── Service ───────────────────────────────────────────────────────────
export const CallbackService = {
  async getAll(): Promise<{ data: Callback[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("callbacks")
      .select("*")
      .order("filed_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as CallbackRow[]).map(toUiCallback), error: null };
  },

  async getByJobId(jobId: string): Promise<{ data: Callback[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("callbacks")
      .select("*")
      .eq("job_id", jobId)
      .order("filed_at", { ascending: false });

    if (error || !data) return { data: null, error };
    return { data: (data as CallbackRow[]).map(toUiCallback), error: null };
  },

  async create(input: Callback): Promise<{ data: Callback | null; error: unknown }> {
    let organizationId: string | null = null;
    try { organizationId = await getCurrentOrganizationId(); } catch { /* proceed without */ }

    const row = toDbRow(input, organizationId);

    const { data, error } = await supabase
      .from("callbacks")
      .insert([row])
      .select()
      .single();

    if (error || !data) {
      console.error("[CallbackService.create] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiCallback(data as CallbackRow), error: null };
  },
};
